import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { mileageRatePresetRepository } from '@webapp/modules/expense/data-access/repositories/MileageRatePresetRepository'
import { MileageRatePresetUnits } from '@webapp/modules/expense/entities/enums'
import messages from './messages'
import { MileageRatePreset } from '../../entities'

export interface IMileageRatePresetSelectProps<
  IsMulti extends boolean = false,
> extends IAsyncSelectProps<MileageRatePreset, IsMulti> {}

export const MileageRatePresetSelect = ({
  cacheOptions = true,
  clearCacheOnMenuClose = false,
  ...props
}: IMileageRatePresetSelectProps) => {
  const intl = useProcurifyIntl()

  const getMileageRatePresetLabel = (value: MileageRatePreset): string => {
    const numberOptions = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 5,
    }

    let amount: string
    try {
      amount = intl.formatNumber(value.rate, {
        ...numberOptions,
        style: 'currency',
        currency: value.currency.name,
        currencyDisplay: 'narrowSymbol',
      })
    } catch {
      amount = intl.formatNumber(value.rate, numberOptions)
    }

    const unitLabel =
      value.unit === MileageRatePresetUnits.MILES
        ? intl.formatMessage(messages.perMile)
        : intl.formatMessage(messages.perKilometer)

    return `${amount} ${unitLabel} - ${value.name}`
  }

  const loadOptions: IAsyncSelectProps<MileageRatePreset>['loadOptions'] =
    async (inputValue, pagination, callback) => {
      try {
        const res = await mileageRatePresetRepository.list({
          search: inputValue,
        })
        const options = res.data.map((preset) => new MileageRatePreset(preset))

        callback({
          options,
          pagination: { hasMore: false, page: pagination.page },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: pagination.page },
        })
      }
    }

  return (
    <AsyncSelect<MileageRatePreset>
      cacheOptions={cacheOptions}
      clearCacheOnMenuClose={clearCacheOnMenuClose}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={(option) => getMileageRatePresetLabel(option)}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
